import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Flame } from "lucide-react";
import { CATEGORIES, DISHES, ORDER_URL } from "../data/menu";

export const MenuSection = () => {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const list = DISHES[active] || [];
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter(
      (d) => d.name.toLowerCase().includes(q) || (d.desc || "").toLowerCase().includes(q)
    );
  }, [active, query]);

  const activeCat = CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-24 md:py-32 bg-brand-cream overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-end mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <span className="font-heading uppercase text-[11px] tracking-[0.3em] font-bold text-brand-red">
              · Explore Our
            </span>
            <h2 className="mt-3 font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] text-brand-charcoal">
              The full <span className="text-brand-red italic">menu.</span>
            </h2>
            <p className="mt-5 text-brand-charcoal/70 max-w-xl leading-relaxed">
              14 categories. 120+ dishes. From street-side snacks to royal biryanis —
              built fresh every single day.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <Search
                size={18}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-charcoal/40"
              />
              <input
                type="text"
                placeholder="Search dishes, ingredients, vibes…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="menu-search-input"
                className="w-full pl-12 pr-5 py-4 rounded-full bg-white border border-brand-border focus:border-brand-red focus:outline-none focus:ring-4 focus:ring-brand-red/10 font-body text-brand-charcoal placeholder:text-brand-charcoal/40 transition"
              />
            </div>
          </motion.div>
        </div>

        {/* Tab pills */}
        <div
          data-testid="menu-category-tabs"
          className="relative -mx-6 md:mx-0 px-6 md:px-0 mb-10"
        >
          <div className="flex gap-2 overflow-x-auto scrollbar-hidden pb-3 snap-x snap-mandatory">
            {CATEGORIES.map((c) => {
              const isActive = c.id === active;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  data-testid={`menu-tab-${c.id}`}
                  className={`shrink-0 snap-start px-5 py-2.5 rounded-full font-heading uppercase text-[11px] tracking-[0.18em] font-bold border transition-all ${
                    isActive
                      ? "bg-brand-red text-brand-cream border-brand-red shadow-[0_8px_24px_-8px_rgba(179,0,27,0.5)]"
                      : "bg-white border-brand-border text-brand-charcoal/75 hover:border-brand-red hover:text-brand-red"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active + query}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8 border-b border-brand-border pb-5">
              <h3 className="font-display uppercase text-3xl md:text-4xl text-brand-charcoal flex items-center gap-3">
                <Flame size={22} className="text-brand-red" />
                {activeCat?.name}
              </h3>
              <p className="font-heading uppercase text-[11px] tracking-[0.2em] font-bold text-brand-charcoal/55">
                {activeCat?.tagline} · {filtered.length} items
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="py-20 text-center text-brand-charcoal/50 font-heading uppercase tracking-wider">
                No dishes match "{query}". Try another word.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
                {filtered.map((d, i) => (
                  <motion.div
                    key={d.name + i}
                    data-testid="menu-dish-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.025 }}
                    className="group flex items-baseline justify-between gap-4 py-4 border-b border-brand-border/60 hover:border-brand-red transition-colors"
                  >
                    <div className="min-w-0">
                      <h4 className="font-heading uppercase text-base md:text-lg tracking-tight font-extrabold text-brand-charcoal group-hover:text-brand-red transition-colors">
                        {d.name}
                      </h4>
                      {d.desc && (
                        <p className="mt-1 text-sm text-brand-charcoal/60 leading-snug">
                          {d.desc}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-display text-xl md:text-2xl text-brand-red whitespace-nowrap">
                        {d.price}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 text-center">
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-order-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-charcoal text-brand-cream font-heading uppercase text-xs tracking-[0.2em] font-bold hover:bg-brand-red transition-all hover:-translate-y-1 shadow-xl"
          >
            Order The Full Menu Online
          </a>
        </div>
      </div>
    </section>
  );
};
